import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { database } from '../firebase';
import { ref, set, onValue, remove } from 'firebase/database';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import './Calendar.css';

const Calendar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [visitorName, setVisitorName] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [visits, setVisits] = useState({});
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({
    visitorName: '',
    visitorEmail: '',
    checkInDate: '',
    checkOutDate: ''
  });

  useEffect(() => {
    const visitsRef = ref(database, 'visits');
    const unsubscribe = onValue(visitsRef, 
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setVisits(data);
        }
        setDataLoading(false);
      }, 
      (error) => {
        console.error('Error loading visits:', error);
        setError('Error al cargar las visitas: ' + error.message);
        setShowError(true);
        setDataLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Sincronizar URL con estado del modal
  useEffect(() => {
    if (openDialog) {
      if (editingVisit) {
        const currentModal = searchParams.get('modal');
        const currentId = searchParams.get('id');
        if (currentModal !== 'edit' || currentId !== editingVisit.id) {
          setSearchParams({ modal: 'edit', id: editingVisit.id }, { replace: true });
        }
      } else {
        if (searchParams.get('modal') !== 'new') {
          setSearchParams({ modal: 'new' }, { replace: true });
        }
      }
    } else {
      // Limpiar params cuando se cierra el modal
      if (searchParams.get('modal')) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('modal');
        newParams.delete('id');
        if (newParams.toString() !== searchParams.toString()) {
          setSearchParams(newParams, { replace: true });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, editingVisit]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    // Solo cambiar la fecha seleccionada, no abrir el modal
  };

  const handleEditVisit = (visitId, visit) => {
    setEditingVisit({ id: visitId, ...visit });
    setVisitorName(visit.name);
    setVisitorEmail(visit.email || '');
    setCheckInDate(visit.checkIn);
    setCheckOutDate(visit.checkOut);
    setFieldErrors({
      visitorName: '',
      visitorEmail: '',
      checkInDate: '',
      checkOutDate: ''
    });
    setOpenDialog(true);
  };

  const validateFields = () => {
    const errors = {
      visitorName: '',
      visitorEmail: '',
      checkInDate: '',
      checkOutDate: ''
    };
    let isValid = true;

    if (!visitorName.trim()) {
      errors.visitorName = 'El nombre del visitante es obligatorio';
      isValid = false;
    }

    if (!visitorEmail.trim()) {
      errors.visitorEmail = 'El email del visitante es obligatorio';
      isValid = false;
    } else if (!visitorEmail.includes('@') || !visitorEmail.includes('.')) {
      errors.visitorEmail = 'Por favor ingresa un email válido';
      isValid = false;
    }

    if (!checkInDate) {
      errors.checkInDate = 'La fecha de entrada es obligatoria';
      isValid = false;
    }

    if (!checkOutDate) {
      errors.checkOutDate = 'La fecha de salida es obligatoria';
      isValid = false;
    } else if (checkInDate && new Date(checkInDate) > new Date(checkOutDate)) {
      errors.checkOutDate = 'La fecha de salida debe ser posterior a la fecha de entrada';
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleAddVisit = async () => {
    // Validar campos antes de continuar
    if (!validateFields()) {
      return;
    }

    setLoading(true);
    try {
      const visitData = {
        name: visitorName,
        email: visitorEmail,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        createdAt: new Date().toISOString()
      };

      let visitId;
      if (editingVisit) {
        // Update existing visit
        visitId = editingVisit.id;
        const visitRef = ref(database, `visits/${editingVisit.checkIn.split('T')[0]}/${editingVisit.id}`);
        await set(visitRef, visitData);
        setEditingVisit(null);
      } else {
        // Add new visit - use checkInDate, not selectedDate
        const dateKey = checkInDate.split('T')[0];
        visitId = Date.now().toString();
        const visitRef = ref(database, `visits/${dateKey}/${visitId}`);
        await set(visitRef, visitData);
      }
      
      // Send confirmation emails via n8n webhook
      // Only send email for new visits, not when editing
      if (!editingVisit) {
        try {
          const webhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL || 'https://tu-n8n-instance.com/webhook/visit-confirmation';
          
          // Format dates for better readability in emails
          // Parse date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss) and create date in local timezone
          const formatDateForEmail = (dateString) => {
            // Extract just the date part (YYYY-MM-DD)
            const dateOnly = dateString.split('T')[0];
            const [year, month, day] = dateOnly.split('-').map(Number);
            // Create date in local timezone (month is 0-indexed in Date constructor)
            const date = new Date(year, month - 1, day);
            return date.toLocaleDateString('es-ES', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          };
          
          const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              visitorName: visitData.name,
              visitorEmail: visitData.email,
              checkIn: visitData.checkIn,
              checkOut: visitData.checkOut,
              checkInFormatted: formatDateForEmail(visitData.checkIn),
              checkOutFormatted: formatDateForEmail(visitData.checkOut),
              visitId: visitId,
              createdAt: visitData.createdAt
            }),
          });

          if (!response.ok) {
            console.error('Error sending email via webhook:', response.statusText);
            // Don't show error to user if email fails, just log it
          }
        } catch (emailError) {
          console.error('Error sending confirmation email:', emailError);
          // Don't block the visit creation if email fails
        }
      }
      
      setVisitorName('');
      setVisitorEmail('');
      setCheckInDate('');
      setCheckOutDate('');
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving visit:', error);
      setError('Error al guardar la visita: ' + error.message);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setVisitorName('');
    setVisitorEmail('');
    setCheckInDate('');
    setCheckOutDate('');
    setEditingVisit(null);
    setFieldErrors({
      visitorName: '',
      visitorEmail: '',
      checkInDate: '',
      checkOutDate: ''
    });
  };

  const handleOpenDialog = () => {
    // Usar la fecha seleccionada en el calendario como fecha de entrada predeterminada
    const selectedDateString = selectedDate.toISOString().split('T')[0];
    setCheckInDate(selectedDateString);
    setCheckOutDate('');
    setVisitorName('');
    setVisitorEmail('');
    setEditingVisit(null);
    setFieldErrors({
      visitorName: '',
      visitorEmail: '',
      checkInDate: '',
      checkOutDate: ''
    });
    setOpenDialog(true);
  };

  const handleDeleteVisit = async (dateKey, visitId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta visita?')) {
      return;
    }
    
    setLoading(true);
    try {
      const visitRef = ref(database, `visits/${dateKey}/${visitId}`);
      await remove(visitRef);
    } catch (error) {
      console.error('Error deleting visit:', error);
      setError('Error al eliminar la visita: ' + error.message);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const getVisitsForDate = () => {
    return getVisitsForDay(selectedDate);
  };

  const getVisitsForDay = (date) => {
    const allVisits = {};
    
    // Iterate through all visits to find ones that span this date
    Object.entries(visits).forEach(([visitDate, dateVisits]) => {
      Object.entries(dateVisits).forEach(([visitId, visit]) => {
        const [checkInYear, checkInMonth, checkInDay] = visit.checkIn.split('T')[0].split('-').map(Number);
        const [checkOutYear, checkOutMonth, checkOutDay] = visit.checkOut.split('T')[0].split('-').map(Number);
        
        const checkIn = new Date(checkInYear, checkInMonth - 1, checkInDay);
        const checkOut = new Date(checkOutYear, checkOutMonth - 1, checkOutDay);
        const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        // Check if the current date falls between check-in and check-out dates
        if (currentDate >= checkIn && currentDate <= checkOut) {
          allVisits[visitId] = visit;
        }
      });
    });
    
    return allVisits;
  };

  const getVisitCount = (date) => {
    const dayVisits = getVisitsForDay(date);
    return Object.keys(dayVisits).length;
  };

  const renderCalendar = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isSelected = date.toDateString() === selectedDate.toDateString();
      const visitCount = getVisitCount(date);
      const hasVisits = visitCount > 0;
      
      days.push(
        <Tooltip 
          key={day}
          title={hasVisits ? `${visitCount} visita${visitCount > 1 ? 's' : ''} programada${visitCount > 1 ? 's' : ''}` : 'Sin visitas'}
          placement="top"
          disableHoverListener={isMobile}
        >
          <div
            className={`calendar-day ${isSelected ? 'selected' : ''} ${hasVisits ? 'has-visits' : ''}`}
            onClick={() => handleDateSelect(date)}
          >
            <span className="day-number">{day}</span>
            {hasVisits && (
              <div className="visit-indicator">
                <EventIcon fontSize={isSmallMobile ? "small" : "medium"} />
                <span className="visit-count">{visitCount}</span>
              </div>
            )}
          </div>
        </Tooltip>
      );
    }
    
    return days;
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const handlePrevMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1));
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return '';
    
    let date;
    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === 'string') {
      const [year, month, day] = dateInput.split('T')[0].split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else {
      return '';
    }
    
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (dataLoading) {
    return (
      <div className="calendar-container">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <CircularProgress size={60} sx={{ color: '#606C38' }} />
          <Typography variant="h6" color="#606C38">
            Cargando calendario...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <h1>Calendario de Visitas</h1>
      </div>

      <div className="calendar-content">
        <Paper elevation={3} className="calendar-paper">
          <div className="calendar-header-controls">
            <Button 
              onClick={handlePrevMonth}
              disabled={loading}
              sx={{ minWidth: 'auto', padding: isMobile ? '0.3rem' : '0.5rem' }}
            >
              &lt;
            </Button>
            <Typography variant="h6">
              {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
            </Typography>
            <Button 
              onClick={handleNextMonth}
              disabled={loading}
              sx={{ minWidth: 'auto', padding: isMobile ? '0.3rem' : '0.5rem' }}
            >
              &gt;
            </Button>
          </div>
          <div className="calendar-weekdays">
            <div>Dom</div>
            <div>Lun</div>
            <div>Mar</div>
            <div>Mié</div>
            <div>Jue</div>
            <div>Vie</div>
            <div>Sáb</div>
          </div>
          <div className="calendar-grid">
            {renderCalendar()}
          </div>
        </Paper>

        <Paper elevation={3} className="visits-paper">
          <Box className="visits-header">
            <Typography variant="h6">
              Visitas para {formatDate(selectedDate)}
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleOpenDialog}
              disabled={loading}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                backgroundColor: '#606C38',
                '&:hover': {
                  backgroundColor: '#283618'
                }
              }}
            >
              Agregar Visita
            </Button>
          </Box>

          <div className="visits-list">
            {Object.entries(getVisitsForDate()).length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '1rem',
                color: '#606C38',
                fontStyle: 'italic',
                fontSize: '0.85rem'
              }}>
                No hay visitas programadas para esta fecha
              </div>
            ) : (
              Object.entries(getVisitsForDate()).map(([visitId, visit]) => (
                <div key={visitId} className="visit-item">
                  <div className="visit-item-header">
                    <span className="visit-item-name">{visit.name}</span>
                    <div className="visit-item-actions">
                      <IconButton 
                        size="small"
                        disabled={loading}
                        onClick={() => handleEditVisit(visitId, visit)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small"
                        disabled={loading}
                        onClick={() => handleDeleteVisit(visit.checkIn.split('T')[0], visitId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </div>
                  <div className="visit-item-dates">
                    <div><strong>Entrada:</strong> {formatDate(visit.checkIn)}</div>
                    <div><strong>Salida:</strong> {formatDate(visit.checkOut)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Paper>
      </div>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingVisit ? 'Editar Visita' : 'Agregar Nueva Visita'}
        </DialogTitle>
        <DialogContent>
          {Object.values(fieldErrors).some(err => err) && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              Por favor corrige los errores en los campos marcados
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Nombre del Visitante"
            fullWidth
            required
            value={visitorName}
            onChange={(e) => {
              setVisitorName(e.target.value);
              if (fieldErrors.visitorName) {
                setFieldErrors({ ...fieldErrors, visitorName: '' });
              }
            }}
            disabled={loading}
            error={!!fieldErrors.visitorName}
            helperText={fieldErrors.visitorName || 'Campo obligatorio'}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            margin="dense"
            label="Email del Visitante"
            type="email"
            fullWidth
            required
            value={visitorEmail}
            onChange={(e) => {
              setVisitorEmail(e.target.value);
              if (fieldErrors.visitorEmail) {
                setFieldErrors({ ...fieldErrors, visitorEmail: '' });
              }
            }}
            disabled={loading}
            error={!!fieldErrors.visitorEmail}
            helperText={fieldErrors.visitorEmail || 'Se enviará una confirmación a este email'}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            margin="dense"
            label="Fecha de Entrada"
            type="date"
            fullWidth
            required
            value={checkInDate}
            onChange={(e) => {
              setCheckInDate(e.target.value);
              if (fieldErrors.checkInDate) {
                setFieldErrors({ ...fieldErrors, checkInDate: '' });
              }
              // Si la fecha de salida es anterior a la nueva fecha de entrada, limpiarla
              if (checkOutDate && new Date(e.target.value) > new Date(checkOutDate)) {
                setCheckOutDate('');
              }
            }}
            disabled={loading}
            error={!!fieldErrors.checkInDate}
            helperText={fieldErrors.checkInDate || 'Campo obligatorio'}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            margin="dense"
            label="Fecha de Salida"
            type="date"
            fullWidth
            required
            value={checkOutDate}
            onChange={(e) => {
              setCheckOutDate(e.target.value);
              if (fieldErrors.checkOutDate) {
                setFieldErrors({ ...fieldErrors, checkOutDate: '' });
              }
            }}
            disabled={loading}
            error={!!fieldErrors.checkOutDate}
            helperText={fieldErrors.checkOutDate || 'Campo obligatorio'}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: checkInDate || undefined
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAddVisit}
            disabled={loading}
            sx={{ 
              backgroundColor: '#606C38',
              color: 'white',
              '&:hover': {
                backgroundColor: '#283618'
              }
            }}
          >
            {loading ? 'Guardando...' : (editingVisit ? 'Guardar Cambios' : 'Agregar')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={showError} 
        autoHideDuration={6000} 
        onClose={() => setShowError(false)}
      >
        <Alert 
          onClose={() => setShowError(false)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Calendar; 